import React from 'react';

import dayjs from '~/timeInit';

import { Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import type { CppInvoice } from '~/services/LuxLoyalty';

import {
  currencyCode,
  formatBandRates,
  formatCurrencyCents,
  formatSignedPoints,
  isEmptyInvoiceDay,
} from './invoiceFormat';
import { withoutFutureDays } from './monthRange';

interface Props {
  invoice: CppInvoice;
  /** Drop days that billed nothing and moved no points. Never hides an unpriced day. */
  hideEmptyDays?: boolean;
}

/**
 * The invoice's line items: one row per day of the billed period.
 *
 * Points are shown as a single signed figure — positive for points issued to members, negative for
 * points debited — rather than split across separate credit and debit columns. Base and bonus points
 * are shown in their own columns; both are billed at the same CPP, so the amount covers the two.
 *
 * The rate card that priced each day is deliberately not shown: it is internal pricing
 * administration, not something the partner is billed for. The CPP the day was charged at stays,
 * since it is what turns points into the amount owed.
 *
 * Days that have not happened yet are omitted — see {@link withoutFutureDays}.
 */
export default function InvoiceLineTable({ invoice, hideEmptyDays = false }: Props) {
  const { currency } = invoice;
  // A period covering the running month comes back with a row for every calendar day, so viewing
  // it mid-month would list a tail of future dates that can only read zero. Display-only: the
  // totals come from the service, and a future day contributes nothing to them either way.
  const datedDays = withoutFutureDays(invoice.days);
  const days = hideEmptyDays ? datedDays.filter((day) => !isEmptyInvoiceDay(day)) : datedDays;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Date</TableCell>
          <TableCell align="right">Points</TableCell>
          <TableCell align="right">Bonus Points</TableCell>
          <TableCell align="right">CPP</TableCell>
          <TableCell align="right">Amount ({currencyCode(currency)})</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {days.length === 0 && (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography variant="body2" color="text.secondary">
                No activity in this period.
              </Typography>
            </TableCell>
          </TableRow>
        )}

        {days.map((day) => (
          <TableRow key={day.date}>
            <TableCell>
              <Typography variant="body2">{dayjs(day.date).format('DD/MM/YYYY')}</Typography>
              {day.refundPoints !== 0 && (
                // The points column carries the day's own activity only — a refund is priced
                // against the day it reverses — so a day with reversals says so, otherwise its
                // amount would look inconsistent with its points.
                <Typography variant="caption" color="text.secondary" display="block">
                  incl. {formatSignedPoints(day.refundPoints)} points reversed
                </Typography>
              )}
              {day.refundBonusPoints !== 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  incl. {formatSignedPoints(day.refundBonusPoints)} bonus points reversed
                </Typography>
              )}
            </TableCell>
            <TableCell align="right">{formatSignedPoints(day.netPoints)}</TableCell>
            <TableCell align="right">{formatSignedPoints(day.netBonusPoints)}</TableCell>
            <TableCell align="right">{formatBandRates(day.tierBreakdown) || '—'}</TableCell>
            <TableCell align="right">
              {day.amountCents === null ? (
                <Tooltip title="No rate card covered this day, so its activity could not be priced">
                  <Chip size="small" color="error" variant="outlined" label="Unpriced" />
                </Tooltip>
              ) : (
                formatCurrencyCents(day.amountCents, currency)
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
